<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';

import Auth from 'dashboard/api/auth';
import Avatar from 'next/avatar/Avatar.vue';
import allLocales from 'shared/constants/locales';
import MobileSettingsHeader from './MobileSettingsHeader.vue';
import MobileAvailabilityToggle from './MobileAvailabilityToggle.vue';
import MobileBottomSheet from './MobileBottomSheet.vue';
import MobileNotificationPrefsView from './MobileNotificationPrefsView.vue';
import { useHaptics } from 'dashboard/composables/useHaptics';
import { vHapticTap as hapticTapDirective } from './hapticTap';
import {
  hasPushPermissions,
  requestPushPermissions,
  unregisterSubscription,
  verifyServiceWorkerExistence,
} from 'dashboard/helper/pushHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper';

const store = useStore();
const { t, locale } = useI18n();
const { enabledLanguages } = useConfig();
const { currentAccount, updateAccount } = useAccount();
const { updateUISettings, uiSettings } = useUISettings();

const currentUser = useMapGetter('getCurrentUser');

const userName = computed(() => currentUser.value?.name || '');
const userEmail = computed(() => currentUser.value?.email || '');
const userAvatar = computed(() => currentUser.value?.avatar_url || '');
const userAvailability = computed(
  () => currentUser.value?.availability_status || 'online'
);
const currentUserAccounts = computed(() => currentUser.value?.accounts || []);
const canSwitchAccount = computed(() => currentUserAccounts.value.length > 1);
const currentLocale = computed(() => uiSettings.value?.locale ?? '');
const isLanguageSheetOpen = ref(false);
const isAccountSheetOpen = ref(false);
const isNotificationPrefsOpen = ref(false);

const { selection } = useHaptics();

// Theme (light/dark/auto): reuses the SAME localStorage key + applier the desktop
// command bar uses, so the choice stays in sync across mobile/desktop.
const colorScheme = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto'
);
const themeOptions = computed(() => [
  {
    key: 'light',
    label: t('MOBILE.SETTINGS.THEME_LIGHT'),
    icon: 'i-lucide-sun',
  },
  {
    key: 'dark',
    label: t('MOBILE.SETTINGS.THEME_DARK'),
    icon: 'i-lucide-moon',
  },
  {
    key: 'auto',
    label: t('MOBILE.SETTINGS.THEME_SYSTEM'),
    icon: 'i-lucide-monitor',
  },
]);
const setTheme = key => {
  selection();
  colorScheme.value = key;
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, key);
  setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
};

// Value-gated wrapper so the trusted-tap overlay applies only to rows that
// opt in (currently just the notifications row), leaving pre-existing rows
// with their original behavior.
const vHapticTap = {
  mounted(el, binding) {
    if (binding.value) hapticTapDirective.mounted(el, binding);
  },
  unmounted(el, binding) {
    hapticTapDirective.unmounted(el, binding);
  },
};

// Push notification state
const pushEnabled = ref(false);
const pushLoading = ref(false);
const pushDenied = ref(false);
const infinitePayPushOnly = ref(false);
const infinitePayPushOnlySaving = ref(false);

const hasPushSupport = computed(
  () =>
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
);

const isStandalone = computed(
  () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
);

const showPushSection = computed(() => hasPushSupport.value);
const showInfinitePayPushOnly = computed(
  () => showPushSection.value && !!currentAccount.value
);

const pushStatusText = computed(() => {
  if (pushDenied.value) return t('MOBILE.PUSH.DENIED');
  if (!isStandalone.value && /iPhone|iPad/.test(navigator.userAgent)) {
    return t('MOBILE.PUSH.IOS_INSTALL_REQUIRED');
  }
  if (pushEnabled.value) return t('MOBILE.PUSH.ENABLED');
  return t('MOBILE.PUSH.DISABLED');
});

const checkPushStatus = () => {
  if (!hasPushSupport.value) return;

  if (Notification.permission === 'denied') {
    pushDenied.value = true;
    pushEnabled.value = false;
    return;
  }

  if (hasPushPermissions()) {
    verifyServiceWorkerExistence(registration =>
      registration.pushManager
        .getSubscription()
        .then(subscription => {
          pushEnabled.value = !!subscription;
        })
        .catch(() => {
          pushEnabled.value = false;
        })
    );
  } else {
    pushEnabled.value = false;
  }
};

watch(
  currentAccount,
  account => {
    infinitePayPushOnly.value = Boolean(
      account?.custom_attributes?.infinitepay_push_only
    );
  },
  { immediate: true }
);

const togglePush = () => {
  if (pushDenied.value) return;
  if (pushLoading.value) return;

  if (pushEnabled.value) {
    // Disable push
    pushLoading.value = true;
    unregisterSubscription(() => {
      pushEnabled.value = false;
    })
      .catch(() => {
        useAlert(t('MOBILE.PUSH.UPDATE_ERROR'));
      })
      .finally(() => {
        pushLoading.value = false;
      });
  } else {
    // Enable push
    pushLoading.value = true;
    requestPushPermissions({
      onSuccess: () => {
        pushEnabled.value = true;
        pushLoading.value = false;
      },
    });
    // Handle case where permission prompt is dismissed or denied
    setTimeout(() => {
      if (pushLoading.value) {
        pushLoading.value = false;
        if (Notification.permission === 'denied') {
          pushDenied.value = true;
        }
      }
    }, 10000);
  }
};

const toggleInfinitePayPushOnly = async () => {
  if (infinitePayPushOnlySaving.value) return;

  const nextValue = !infinitePayPushOnly.value;
  infinitePayPushOnly.value = nextValue;
  infinitePayPushOnlySaving.value = true;

  try {
    await updateAccount(
      {
        infinitepay_push_only: nextValue,
      },
      { silent: true }
    );
    useAlert(t('GENERAL_SETTINGS.FORM.INFINITEPAY.API.SUCCESS'));
  } catch (error) {
    infinitePayPushOnly.value = !nextValue;
    useAlert(t('GENERAL_SETTINGS.FORM.INFINITEPAY.API.ERROR'));
  } finally {
    infinitePayPushOnlySaving.value = false;
  }
};

onMounted(() => {
  checkPushStatus();
});

const onAvailabilityChange = status => {
  store.dispatch('updateAvailability', {
    availability: status,
    account_id: currentUser.value.account_id,
  });
};

const onLogout = () => {
  Auth.logout();
};

const accountDefaultLanguage = computed(() => {
  const accountLocale = currentAccount.value?.locale;
  return allLocales[accountLocale] || accountLocale || '';
});

const languageOptions = computed(() => [
  {
    name: t('MOBILE.SETTINGS.USE_ACCOUNT_DEFAULT'),
    iso_639_1_code: '',
  },
  ...(enabledLanguages ?? []),
]);

const currentLanguageLabel = computed(() => {
  if (!currentLocale.value) {
    return (
      accountDefaultLanguage.value || t('MOBILE.SETTINGS.USE_ACCOUNT_DEFAULT')
    );
  }

  const selectedLanguage = languageOptions.value.find(
    option => option.iso_639_1_code === currentLocale.value
  );

  return (
    selectedLanguage?.name ||
    allLocales[currentLocale.value] ||
    currentLocale.value
  );
});

const sortedAccounts = computed(() => {
  return [...currentUserAccounts.value].sort((firstAccount, secondAccount) =>
    firstAccount.name.localeCompare(secondAccount.name)
  );
});

const currentAccountLabel = computed(() => currentAccount.value?.name || '');

const onOpenLanguageSheet = () => {
  isLanguageSheetOpen.value = true;
};

const onOpenAccountSheet = () => {
  if (!canSwitchAccount.value) return;
  isAccountSheetOpen.value = true;
};

const updateLanguage = async languageCode => {
  try {
    if (!languageCode) {
      await updateUISettings({ locale: null });
      locale.value =
        currentAccount.value?.locale || window.chatwootConfig.selectedLocale;
      useAlert(t('MOBILE.SETTINGS.LANGUAGE_UPDATED'));
      isLanguageSheetOpen.value = false;
      return;
    }

    const isValidLanguage = (enabledLanguages || []).some(
      option => option.iso_639_1_code === languageCode
    );

    if (!isValidLanguage) {
      throw new Error(`Invalid language code: ${languageCode}`);
    }

    await updateUISettings({ locale: languageCode });
    locale.value = languageCode;
    useAlert(t('MOBILE.SETTINGS.LANGUAGE_UPDATED'));
    isLanguageSheetOpen.value = false;
  } catch (error) {
    useAlert(t('MOBILE.SETTINGS.LANGUAGE_UPDATE_ERROR'));
  }
};

const changeAccount = accountId => {
  if (!accountId || accountId === currentAccount.value?.id) {
    isAccountSheetOpen.value = false;
    return;
  }

  window.location.href = `/app/accounts/${accountId}/dashboard`;
};

const onOpenNotificationPrefs = () => {
  selection();
  isNotificationPrefsOpen.value = true;
};

const settingsItems = computed(() => [
  {
    icon: 'i-lucide-bell-ring',
    label: t('MOBILE.SETTINGS.NOTIFICATIONS'),
    action: 'notification-preferences',
    onClick: onOpenNotificationPrefs,
    haptic: true,
  },
  {
    icon: 'i-lucide-globe',
    label: t('MOBILE.SETTINGS.LANGUAGE'),
    action: 'language',
    value: currentLanguageLabel.value,
    onClick: onOpenLanguageSheet,
  },
  {
    icon: 'i-lucide-refresh-cw',
    label: t('MOBILE.SETTINGS.SWITCH_ACCOUNT'),
    action: 'switch-account',
    value: currentAccountLabel.value,
    onClick: onOpenAccountSheet,
    disabled: !canSwitchAccount.value,
  },
]);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto">
    <MobileSettingsHeader />

    <!-- User Profile Section -->
    <div class="flex items-center gap-3 px-4 py-5 border-b border-n-weak">
      <Avatar :src="userAvatar" :name="userName" :size="56" />
      <div class="flex flex-col min-w-0">
        <span class="text-base font-semibold text-n-slate-12 truncate">
          {{ userName }}
        </span>
        <span class="text-sm text-n-slate-10 truncate">
          {{ userEmail }}
        </span>
      </div>
    </div>

    <!-- Availability -->
    <div class="px-4 py-4 border-b border-n-weak">
      <span
        class="text-xs font-medium text-n-slate-10 uppercase tracking-wider mb-2 block"
      >
        {{ t('MOBILE.SETTINGS.AVAILABILITY') }}
      </span>
      <MobileAvailabilityToggle
        :current-status="userAvailability"
        @change="onAvailabilityChange"
      />
    </div>

    <!-- Appearance / Theme -->
    <div class="px-4 py-4 border-b border-n-weak">
      <span
        class="text-xs font-medium text-n-slate-10 uppercase tracking-wider mb-2 block"
      >
        {{ t('MOBILE.SETTINGS.APPEARANCE') }}
      </span>
      <div class="flex items-stretch gap-1 p-1 rounded-xl bg-n-alpha-1">
        <button
          v-for="option in themeOptions"
          :key="option.key"
          class="flex flex-col items-center justify-center gap-1 flex-1 py-2 rounded-lg text-xs font-medium transition-colors"
          :class="
            colorScheme === option.key
              ? 'bg-n-background text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 active:bg-n-alpha-1'
          "
          @click="setTheme(option.key)"
        >
          <span class="size-5" :class="option.icon" />
          {{ option.label }}
        </button>
      </div>
    </div>

    <!-- Push Notifications -->
    <div v-if="showPushSection" class="px-4 py-4 border-b border-n-weak">
      <span
        class="text-xs font-medium text-n-slate-10 uppercase tracking-wider mb-3 block"
      >
        {{ t('MOBILE.PUSH.TITLE') }}
      </span>
      <button
        class="flex items-center w-full gap-3 py-1"
        :class="{ 'opacity-50': pushDenied }"
        @click="togglePush"
      >
        <span class="i-lucide-bell size-5 text-n-slate-12" />
        <div class="flex flex-col items-start min-w-0 flex-1">
          <span class="text-sm text-n-slate-12">
            {{ t('MOBILE.PUSH.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-10 truncate w-full text-left">
            {{ pushStatusText }}
          </span>
        </div>
        <div
          class="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
          :class="pushEnabled ? 'bg-n-brand' : 'bg-n-slate-7'"
        >
          <span
            v-if="pushLoading"
            class="absolute inset-0 flex items-center justify-center"
          >
            <span class="i-lucide-loader-2 size-3.5 text-white animate-spin" />
          </span>
          <span
            v-else
            class="absolute top-0.5 ltr:left-0.5 rtl:right-0.5 size-5 rounded-full bg-white shadow transition-transform"
            :class="
              pushEnabled
                ? 'ltr:translate-x-5 rtl:-translate-x-5'
                : 'translate-x-0'
            "
          />
        </div>
      </button>
      <p v-if="pushDenied" class="text-xs text-n-ruby-9 mt-2 px-8">
        {{ t('MOBILE.PUSH.DENIED_HELP') }}
      </p>

      <button
        v-if="showInfinitePayPushOnly"
        class="flex items-start w-full gap-3 py-3 mt-3 border-t border-n-weak"
        :disabled="infinitePayPushOnlySaving"
        @click="toggleInfinitePayPushOnly"
      >
        <span
          class="i-lucide-badge-dollar-sign size-5 text-n-slate-12 mt-0.5"
        />
        <div class="flex flex-col items-start min-w-0 flex-1 text-left">
          <span class="text-sm text-n-slate-12">
            {{ t('GENERAL_SETTINGS.FORM.INFINITEPAY.PUSH_ONLY_LABEL') }}
          </span>
          <span class="text-xs text-n-slate-10 leading-5">
            {{ t('GENERAL_SETTINGS.FORM.INFINITEPAY.PUSH_ONLY_NOTE') }}
          </span>
          <span v-if="infinitePayPushOnly" class="text-xs text-n-slate-11 mt-1">
            {{ t('GENERAL_SETTINGS.FORM.INFINITEPAY.PUSH_ONLY_ENABLED') }}
          </span>
        </div>
        <div
          class="relative w-11 h-6 rounded-full transition-colors flex-shrink-0 mt-0.5"
          :class="infinitePayPushOnly ? 'bg-n-brand' : 'bg-n-slate-7'"
        >
          <span
            v-if="infinitePayPushOnlySaving"
            class="absolute inset-0 flex items-center justify-center"
          >
            <span class="i-lucide-loader-2 size-3.5 text-white animate-spin" />
          </span>
          <span
            v-else
            class="absolute top-0.5 ltr:left-0.5 rtl:right-0.5 size-5 rounded-full bg-white shadow transition-transform"
            :class="
              infinitePayPushOnly
                ? 'ltr:translate-x-5 rtl:-translate-x-5'
                : 'translate-x-0'
            "
          />
        </div>
      </button>
    </div>

    <!-- Settings List -->
    <div class="flex flex-col">
      <button
        v-for="item in settingsItems"
        :key="item.action"
        v-haptic-tap="item.haptic"
        class="flex items-center gap-3 px-4 py-3.5 text-left text-n-slate-12 active:bg-n-alpha-1 border-b border-n-weak"
        :class="{ 'opacity-60': item.disabled }"
        :disabled="item.disabled"
        @click="item.onClick"
      >
        <span class="size-5" :class="item.icon" />
        <div class="min-w-0 flex-1">
          <span class="text-sm block">{{ item.label }}</span>
          <span
            v-if="item.value"
            class="text-xs text-n-slate-10 truncate block"
          >
            {{ item.value }}
          </span>
        </div>
        <span class="i-lucide-chevron-right size-4 ml-auto text-n-slate-10" />
      </button>
    </div>

    <!-- Logout -->
    <div class="px-4 py-4 mt-auto">
      <button
        class="flex items-center justify-center gap-2 w-full py-3 text-sm font-medium text-n-ruby-9 rounded-lg border border-n-weak active:bg-n-alpha-1"
        @click="onLogout"
      >
        <span class="i-lucide-log-out size-4" />
        {{ t('MOBILE.SETTINGS.LOGOUT') }}
      </button>
    </div>

    <MobileNotificationPrefsView
      v-if="isNotificationPrefsOpen"
      @back="isNotificationPrefsOpen = false"
    />

    <MobileBottomSheet
      v-if="isLanguageSheetOpen"
      :title="t('MOBILE.SETTINGS.LANGUAGE')"
      @close="isLanguageSheetOpen = false"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="option in languageOptions"
          :key="option.iso_639_1_code || 'account-default'"
          class="flex items-center gap-3 w-full rounded-xl border border-n-weak px-4 py-3 text-left active:bg-n-alpha-1"
          @click="updateLanguage(option.iso_639_1_code)"
        >
          <span class="flex-1 min-w-0">
            <span class="text-sm text-n-slate-12 block truncate">{{
              option.name
            }}</span>
            <span
              v-if="!option.iso_639_1_code && accountDefaultLanguage"
              class="text-xs text-n-slate-10 block truncate"
            >
              {{ accountDefaultLanguage }}
            </span>
          </span>
          <span
            v-if="
              option.iso_639_1_code === currentLocale ||
              (!option.iso_639_1_code && !currentLocale)
            "
            class="i-lucide-check size-4 text-n-brand flex-shrink-0"
          />
        </button>
      </div>
    </MobileBottomSheet>

    <MobileBottomSheet
      v-if="isAccountSheetOpen"
      :title="t('MOBILE.SETTINGS.SWITCH_ACCOUNT')"
      @close="isAccountSheetOpen = false"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="account in sortedAccounts"
          :key="account.id"
          class="flex items-center gap-3 w-full rounded-xl border border-n-weak px-4 py-3 text-left active:bg-n-alpha-1"
          @click="changeAccount(account.id)"
        >
          <span class="flex-1 min-w-0">
            <span class="text-sm text-n-slate-12 block truncate">{{
              account.name
            }}</span>
            <span class="text-xs text-n-slate-10 block truncate capitalize">{{
              account.custom_role_id ? account.custom_role.name : account.role
            }}</span>
          </span>
          <span
            v-if="account.id === currentAccount?.id"
            class="i-lucide-check size-4 text-n-brand flex-shrink-0"
          />
        </button>
      </div>
    </MobileBottomSheet>
  </div>
</template>
